import { gql } from "@apollo/client";

export const get_treatment_report = gql`
  query CheckIns(
    $where: CheckInWhereInput
    $orderBy: [CheckInOrderByWithRelationInput!]
    $take: Int
    $skip: Int
    $clinicMembersWhere2: ClinicMemberWhereInput
  ) {
    checkIns(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      member {
        name
        clinic_members(where: $clinicMembersWhere2) {
          name
        }
      }
      practitioner {
        id
        name
      }
      status
      in_time
      service {
        id
        name
      }
      booking {
        from_time
        to_time
        service_helper {
          name
          id
        }
      }
      orders {
        order_items {
          id
          price
          service_id
        }
      }
     isUsePurchaseService
    }
  }
`;

const getPractitionerWhereFilter = (practitionerId: string) => ({
  OR: [
    { practitioner_id: { equals: practitionerId } },
    {
      practitioners: {
        some: {
          practitioner_id: { equals: practitionerId },
        },
      },
    },
  ],
});

const buildTreatmentReportWhere = (data: {
  clinicId: string;
  startDate: Date;
  endDate: Date;
  status?: string[] | string;
  searchText?: string | null;
  practitionerId?: string;
}) => {
  const status = Array.isArray(data.status)
    ? data.status
    : data.status
      ? [data.status]
      : [];
  const { searchText } = data;
  const where: any = {};
  const searchOR: any[] = [];

  if (searchText && searchText !== "") {
    searchOR.push({
      member: {
        is: {
          OR: [
            { name: { contains: searchText } },
            { phonenumber: { contains: searchText } },
          ],
        },
      },
    });
    searchOR.push({
      practitioner: {
        is: {
          OR: [
            { name: { contains: searchText } },
            { phonenumber: { contains: searchText } },
          ],
        },
      },
    });
    searchOR.push({
      service: { is: { OR: [{ name: { contains: searchText } }] } },
    });
  }

  where.clinic_id = { equals: data.clinicId };
  where.in_time = { gte: data.startDate, lte: data.endDate };
  if (status.length > 0) where.status = { in: status };

  const filters: any[] = [];
  if (data.practitionerId) {
    filters.push(getPractitionerWhereFilter(data.practitionerId));
  }
  if (searchOR.length > 0) {
    filters.push({ OR: searchOR });
  }
  if (filters.length === 1) {
    Object.assign(where, filters[0]);
  } else if (filters.length > 1) {
    where.AND = filters;
  }

  return where;
};

export const getTreatmentReportVar = (data: {
  clinicId: string;
  startDate: Date;
  endDate: Date;
  status?: string[] | string;
  searchText?: string | null;
  take: number;
  skip: number;
  practitionerId?: string;
}) => {
  const variables: any = {
    where: buildTreatmentReportWhere(data),
    clinicMembersWhere2: { clinic_id: { equals: data.clinicId } },
    orderBy: [{ created_at: "desc" }],
    take: data.take,
    skip: data.skip,
  };
  return variables;
};

export const get_treatment_count = gql`
  query GroupByCheckIn(
    $by: [CheckInScalarFieldEnum!]!
    $where: CheckInWhereInput
  ) {
    groupByCheckIn(by: $by, where: $where) {
      _count {
        _all
      }
    }
  }
`;

export const getTreatmentCountVar = (data: {
  clinicId: string;
  startDate: Date;
  endDate: Date;
  status?: string | string[];
  practitionerId?: string;
}) => {
  let variables: any = {};
  variables.by = ["clinic_id", "status"];
  variables.where = {
    clinic_id: { equals: data.clinicId },
    in_time: { gte: data.startDate, lte: data.endDate },
    status: { in: data.status },
  };

  if (data.practitionerId) {
    variables.where = {
      ...variables.where,
      ...getPractitionerWhereFilter(data.practitionerId),
    };
  }
  return variables;
};

export const export_treatments = gql`
  query CheckIns(
    $where: CheckInWhereInput
    $orderBy: [CheckInOrderByWithRelationInput!]
    $take: Int
    $skip: Int
    $clinicMembersWhere2: ClinicMemberWhereInput
  ) {
    checkIns(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      member {
        name
        clinic_members(where: $clinicMembersWhere2) {
          name
        }
      }
      practitioner {
        id
        name
      }
      status
      in_time
      service {
        id
        name
      }
      booking {
        from_time
        to_time
        service_helper {
          name
          id
        }
      }
      orders {
        order_items {
          id
          price
          service_id
        }
      }
      isUsePurchaseService
    }
  }
`;

export const getExportTreatmentVar = (data: {
  clinicId: string;
  startDate: Date;
  endDate: Date;
  status?: string | string[];
  searchText?: string | null;
  take: number;
  skip: number;
  practitionerId?: string;
}) => ({
  where: buildTreatmentReportWhere({
    ...data,
    status: data.status ?? "CHECKOUT",
  }),
  clinicMembersWhere2: { clinic_id: { equals: data.clinicId } },
  orderBy: [{ created_at: "desc" }],
  take: data.take,
  skip: data.skip,
});
